import axios from "axios";
import { dispatchNotificationsRefresh } from "./notificationEvents";

export class BaseAPIClient {
  constructor(baseURL = process.env.VUE_APP_PITCH_API) {
    this.instance = axios.create({
      baseURL,
    });

    // Controle de refresh: evita disparar varias renovacoes simultaneas quando
    // multiplas requisicoes recebem 401 ao mesmo tempo.
    this.isRefreshing = false;
    this.refreshQueue = [];

    this.instance.interceptors.response.use(
      (response) => {
        const method = String(response?.config?.method || "").toUpperCase();
        const hasToken = !!localStorage.getItem("token");

        if (hasToken && ["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
          dispatchNotificationsRefresh();
        }

        return response;
      },
      async (error) => {
        const original = error.config || {};
        const status = error.response?.status;

        // 401: tenta renovar o token uma unica vez e refaz a requisicao original.
        // Nunca renova a propria chamada de refresh nem repete a tentativa.
        if (
          status === 401 &&
          !original._retry &&
          !original._isRefreshCall &&
          localStorage.getItem("refreshToken")
        ) {
          original._retry = true;
          try {
            const newToken = await this.refreshAccessToken();
            original.headers = {
              ...(original.headers || {}),
              Authorization: "Bearer " + newToken,
              refreshToken: "Bearer " + localStorage.getItem("refreshToken"),
            };
            return this.instance.request(original);
          } catch (refreshError) {
            this.forceLogout();
            return Promise.reject(refreshError);
          }
        }

        if (status === 401 || status === 403) {
          this.forceLogout();
        }

        return Promise.reject(error);
      }
    );
  }

  forceLogout() {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    // Limpa o estado persistido do Vuex para o guard de rota nao reentrar na app
    // com um token invalido apos o reload.
    localStorage.removeItem("session");
    window.location.href = "/connect";
  }

  async refreshAccessToken() {
    // Se ja existe um refresh em andamento, aguarda o mesmo resultado.
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const { data } = await this.instance.request({
        url: "/refresh-token",
        method: "POST",
        _isRefreshCall: true,
        headers: { refreshToken: "Bearer " + refreshToken },
        data: { refreshToken },
      });

      const newToken = data.auth || data.token;
      localStorage.setItem("token", newToken);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }

      this.refreshQueue.forEach((pending) => pending.resolve(newToken));
      this.refreshQueue = [];
      return newToken;
    } catch (error) {
      this.refreshQueue.forEach((pending) => pending.reject(error));
      this.refreshQueue = [];
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async call(path, method, data, additionalHeaders = undefined) {
    return await new Promise((resolve, reject) => {
      const request = {
        url: path,
        method,
        data,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          refreshToken: "Bearer " + localStorage.getItem("refreshToken"),
          ...additionalHeaders,
        },
      };
      if (method === "GET" && data) {
        request.params = data;
      }

      this.instance
        .request(request)
        .then((result) => {
          return resolve(result);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  }

  async uploadFiles(formData) {
    return this.call("/avatar", "POST", formData, {
      "Content-Type": "multipart/form-data",
    });
  }
}
